import http from '@/utils/http';
import { getAuthorization } from '@/utils/utils';
import { ICOSConfig, TDeleteHistory, TUpdateHistory } from './_type';
import { v4 as uuidv4 } from 'uuid';
import { CreateSnapshotData, GetSnapshotDataType } from '@/pages/aigc/pages/Agent/ChatShare/types/snapshotDataTypes';
import { processData, transformMessages } from '../ChatPage/utils';
import { history } from 'umi';
export type StreamCallback = (chunk: string) => void;
export type ErrorCallback = (error: Error) => void;
export type CompleteCallback = () => void;

// fetch 获取chat流式数据
interface StreamOptions {
  onChunk?: StreamCallback;
  onError?: ErrorCallback;
  onComplete?: CompleteCallback;
  signal?: AbortSignal;
}

interface StreamResponse<T> {
  data: T | null;
  error: Error | null;
}

export class StreamFetch {
  private baseUrl: string;
  private headers: HeadersInit;

  constructor(baseUrl = '', headers: HeadersInit = {}) {
    this.baseUrl = baseUrl;
    this.headers = {
      'Content-Type': 'application/json' ,
      ...headers,
    };
  }

  async stream(
    url: string,
    options: StreamOptions = {},
    requestInit: RequestInit = {},
  ): Promise<StreamResponse<string>> {
    const fullUrl = this.baseUrl + url;
    const { onChunk, onError, onComplete, signal } = options;

    try {
      const response = await fetch(fullUrl, {
        ...requestInit,
        headers: {
          ...this.headers,
          ...requestInit.headers,
        },
        signal,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorDataCode = errorData?.error?.code || response.status;
        throw new Error(errorDataCode);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let result = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete?.();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        result += chunk;
        onChunk?.(chunk);
      }

      return { data: result, error: null };
    } catch (error) {
      // const err = error instanceof Error ? error : new Error(String(error));
      onError?.(error?.message);
      return { data: null, error: error?.message };
    }
  }

  async post<T = any>(
    url: string,
    body: any,
    options: StreamOptions = {},
  ): Promise<StreamResponse<T>> {
    return this.stream(url, options, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<StreamResponse<T>>;
  }

  async get<T = any>(
    url: string,
    options: StreamOptions = {},
  ): Promise<StreamResponse<T>> {
    return this.stream(url, options, {
      method: 'GET',
    }) as Promise<StreamResponse<T>>;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  static createAbortController(): AbortController {
    return new AbortController();
  }
}

/**
 * 发送语音数据到后端进行转文字处理
 * @param audioBlob 录制的音频数据
 * @returns 转换后的文本内容
 */
export const speechToText = async (
  audioBlob: Blob,
  openId: string,
): Promise<{ text: string; }> => {
  try {
    const formData = new FormData();
    formData.append('audioFile', audioBlob, 'recording.wav');
    formData.append('appId', 'ca2c83b2-64e5-4c5e-b8f7-bab81bbe168e');
    formData.append('openId', openId || '1234567890');

    const response = await fetch('/api/v1/speechai/asr', {
      method: 'POST',
      headers: {
        Authorization: getAuthorization() || '',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Speech to text error:', error);
    throw error;
  }
};

// 获取<你可能还想问>的推荐问题
export const getRecommend = async (data: any): Promise<any> => {
  try {
    const response = await fetch('/api/agent_guessnext', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// history 根据sessionID查询历史数据
export const getHistoryBySession = (params: {
  user_id: string;
  session_id: string;
}): Promise<{
  res_code: number;
  messages?: any;
}> => new Promise((resolve) => {
  fetch('/api/agent/get_history_list', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthorization() || '',
    },
    body: JSON.stringify(params),
  })
    .then(async (response) => {
      // 处理HTTP状态码
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw {
          code: response.status,
          msg: error.msg || `HTTP Error ${response.status}`,
          response,
        };
      }
      const data = await response.json();

      if (data.res_code === 0) {
        resolve({
          res_code: 0,
          messages: data.messages || [],
        });
      } else {
        throw {
          code: data.res_code || 999,
          msg: data.msg || 'Unknown error',
          data,
        };
      }
    })
    .catch((error) => {
      console.error('Fetch error:', error);
      throw error;
    });
});

/**
 * 获取聊天会话列表
 * @returns Promise<Array> 返回会话列表数组,失败返回空数组
 */
export const getChatList: (showErrorTips: (code: number) => void) => Promise<any> = (showErrorTips) => {
  const options = {
    url: '/api/agent/get_chat_list',
    method: 'post',
    original: true,
    ignoreCode: true,
    ignoreRequestId: false,
    data: {
      trace_id: uuidv4(),
    }
  };

  return http(options).then((response: any) => {
    showErrorTips?.(response?.data?.res_code);
    return response?.data || {};
  }, () =>{});
};

/**
 * 更新聊天会话信息
 * @param data 更新参数对象
 * @param data.sessionId 会话ID
 * @param data.title 会话标题
 * @param data.isDelete 是否删除,true表示删除,false表示更新
 * @param data.openid 用户唯一标识
 * @returns Promise<Array> 返回更新后的会话列表数组,失败返回空数组
 */

export const updateChat: (data: {sessionId: string; title: string; isDelete?: boolean; isBookmarked?: boolean; openid: string;}, showErrorTips: (code: number) => void) => Promise<any> = (data, showErrorTips) => {
  const { sessionId, title, isDelete = false, isBookmarked = false, openid } = data;
  const options = {
    url: '/api/agent/update_chat',
    method: 'post',
    original: true,
    trace_id: uuidv4(),
    ignoreCode: true,
    data: {
      user_id: openid,
      session_id: sessionId,
      data: {
        title,
        delete: isDelete === true ? 1 : 0,
        bookmark: isBookmarked
      }
    }
  };

  return http(options).then((response: any) => {
    if (response?.data?.res_code === 0) {
      return true;
    }
    return showErrorTips?.(response?.data?.res_code, response?.data?.res_msg || '更新失败');
  }, (err) => Promise.reject(err || '更新失败'));
};

export const getHistoryList = (data: {sessionId: string; openid: string;}, showErrorTips: (code: number) => void) => {
  const { sessionId, openid } = data;
  const options = {
    url: '/api/agent/get_history_list',
    method: 'post',
    original: true,
    ignoreCode: true,
    data: {
      user_id: openid,
      session_id: sessionId,
      count: 999,
      trace_id: uuidv4(),
    }
  };

  return http(options).then((response: any) => {
    if (response?.data?.res_code === 0) {
      return response?.data;
    }
    return showErrorTips?.(response?.data?.res_code, response?.data?.res_msg || '获取数据失败');
  }, (err) => Promise.reject(err || '更新失败'));
};


/**
 * 删除历史消息记录
 * @param params 删除参数，包含用户ID、追踪ID、会话ID和消息ID列表
 * @returns 返回删除操作的结果数据，失败时返回空数组
 */
export const deleteHistory: (params: TDeleteHistory) => Promise<any> = (params: TDeleteHistory) => {
  const { openId, traceId, sessionId, messageIds } = params;
  const options = {
    url: '/api/agent/delete_history',
    method: 'post',
    original: true,
    ignoreCode: true,
    errorHandler: true,
    data: {
      user_id: openId,
      trace_id: traceId,
      session_id: sessionId,
      message_ids: messageIds
    }
  };

  return http(options).then((response: any) => {
    if (response?.data?.res_code === 0) {
      return response?.data;
    }
    return Promise.reject(response?.data?.res_msg || 'deleteHistory 更新数据失败');
  }, (err) => Promise.reject(err || 'deleteHistory 更新失败'));
};

/**
 * 更新历史数据
 * @param params 更新参数，包含用户ID、追踪ID、会话ID、消息ID和更新数据
 * @returns 错误码，错误信息
 */
export const updateHistory: (params: TUpdateHistory,showErrorTips: (code: number) => void) => Promise<any> = (params: TUpdateHistory,showErrorTips) => {
  const { openId, traceId, sessionId, messageId, data } = params;
  const options = {
    url: '/api/agent/update_history',
    method: 'post',
    original: true,
    ignoreCode: true,
    data: {
      trace_id: traceId,
      user_id: openId,
      session_id: sessionId,
      message_id: messageId,
      data,
    }
  };

  return http(options).then((response: any) => {
    if (response?.data?.res_code === 0) {
      return response?.data;
    }
    return showErrorTips?.(response?.data?.res_code, response?.data?.res_msg || 'updateHistory 更新数据失败');
  }, (err) => Promise.reject(err || 'updateHistory 更新失败'));
};

/**
 * 创建快照用于分享
 * @param sessionId 会话ID
 */
export const createSnapshot = (sessionId: string): Promise<CreateSnapshotData> => {
  const options = {
    url: '/api/agent/create_snapshot',
    method: 'post',
    original: true,         // optional if you want full response
    ignoreCode: true,       // skip code check to manually handle it
    errorHandler: true,
    data: {
      session_id: sessionId,
    },
  };

  return http(options).then((response) => {
    const res = (response?.data ?? {}) as CreateSnapshotData;
    if (res?.res_code === 0) {
      return res;
    }

    return Promise.reject(res.res_msg || '创建快照失败');
  }, (err) => Promise.reject(err || '创建快照失败'));
};

/**
 * 获取 snapshot 数据（根据 snapshot_id 获取历史聊天记录）
 * @param params 查询参数
 * @returns 返回历史消息数据列表
 */
export const  getSnapshotData = (
  params: {
    traceId: string;
    snapshotId: string;
    page?: number;
    count?: number;
    isPool?: boolean;
    sessionId?: string;
    user_id?: string;
    message_id?: string;
  },
  showErrorTips: (code: number, msg?: string) => void,
): Promise<GetSnapshotDataType> => {
  const { traceId, snapshotId, page = 0, count = 999, isPool = false, message_id } = params;

  const options = {
    url: isPool ? '/api/agent_com/get_all_message_in_session' : '/api/agent/get_snapshot',
    method: 'post',
    original: true,
    ignoreCode: true,
    data: {
      trace_id: traceId,
      snapshot_id: snapshotId,
      page,
      count,
    },
  };
  if(isPool) {
    options.data.session_id = params.sessionId;
    options.data.user_id = params.user_id;
    if (message_id) {
      options.data.message_id = params.message_id;
    }
  }
  return http(options).then(
    async (response: { data?: GetSnapshotDataType; }) => {
      if (!response?.data) throw new Error('getSnapshotData response is null or undefined');
      if (response?.data?.res_code === 1005){
        history.push('/agent/404');
      }
      if (response.data.res_code === 0) {
        // return response.data;
        let newFormatData = transformMessages(response.data?.messages);
        newFormatData = await processData(newFormatData);
        return {...response, messages: newFormatData,snapshot_id: response.data.snapshot_id, user_id: response.data.user_id};
      }
      return showErrorTips?.(response.data.res_code, response.data.res_msg || '获取 snapshot 数据失败');
    },
    (err) => Promise.reject(err || 'getSnapshotData 调用失败'),
  );
};

export const getCOSConfig = (): Promise<ICOSConfig> => http({
  url: '/api/v1/util/cos/auth',
  method: 'post',
  data: {
    // // cos类型，agentCos就是上面配置的key
    "cos_type": "agentCos", 
  }
}) as Promise<ICOSConfig>;

export const getCOSPresigned = (data): Promise<ICOSConfig> => http({
  url: '/api/v1/util/cos/presigned_url',
  method: 'post',
  data
}) as Promise<ICOSConfig>;

export const uploadLKEFile = (data): Promise<any> => http({
  url: `/api/agent/upload_lke_file`,
  method: 'post',
  data,
  rawData: true,
  ignoreCode: true,
}) as Promise<any>;

export interface SearchSessionQueryResponse {
  res_code: number;
  res_msg: string;
  chats: {
    session_id: string;
    title: string;
    has_sensitive_data: boolean; 
    timestamp: string;
    keyword: string;
    messages: {
      message_id: string;
      query: {
        parts:{text: string; type: string;}[];
      };
      timestamp:string;
    }[];
  }[];

}

export const getSearchSessionQuery = (params: {
  trace_id: string;
  query: string;
  session_count?: number;
  message_count?: number;
},
showErrorTips: (code: number, msg?: string) => void,
): Promise<SearchSessionQueryResponse['chats']> => {
  const options = {
    url: '/api/agent_com/search_session',
    method: 'post',
    original: true,
    ignoreCode: true,
    data: params,
  };

  return http(options)
    .then(
      (response: { data: SearchSessionQueryResponse; }) => {
        if (!response?.data) throw new Error('SearchSessionQuery response is null or undefined');
        if (response?.data?.res_code === 1005){
          history.push('/agent/404');
        }
        if (response.data.res_code === 0) {
          return response.data.chats.map(chat => ({...chat, keyword: params.query}));
        }
        return showErrorTips?.(response.data.res_code, response.data.res_msg || '获取 SearchSessionQuery 数据失败');
      },
      (err) => Promise.reject(err || 'SearchSessionQuery 调用失败'),
    );
};

export interface DSAgentModelResponse {
  res_code: number;
  res_msg: string;
  agent_models: {
    model_id: string;
    model_name: string;
    agent_type: string;
    title: string;
    description: string;
    creator: string;
    creator_img: string;
    game_codes: string[];
    has_permission: boolean;
    agent_banner: string;
    startup_prompt: string;
  };
}

export const getDSAgentModel = (params: {
  trace_id: string;
  agent_type?: string;
  system_language: 'zh-CN' | 'en-US' | string;
},
showErrorTips: (code: number, msg?: string) => void,
): Promise<DSAgentModelResponse['agent_models'][]> => {
  const options = {
    url: '/api/agent_com/get_agent_models',
    method: 'POST',
    original: true,
    ignoreCode: true,
    data: params,
  };

  return http(options)
    .then(
      (response: { data: DSAgentModelResponse; }) => {
        if (!response?.data || !response?.data.agent_models) {
          const err = new Error(response.data.res_code.toString());
          err.message = response.data.res_msg;
          showErrorTips(response.data.res_code, response.data.res_msg || '获取 DSAgentModels 数据失败');
          throw err;
        }

        if (response?.data?.res_code === 1005){
          history.push('/agent/404');
        }

        if (response.data.res_code === 0) {
          return response.data.agent_models;
        }

        return showErrorTips(response.data.res_code, response.data.res_msg || '获取 DSAgentModels 数据失败');
      },
      (err) => Promise.reject(err || 'DSAgentModels 调用失败'),
    );
};